// Default threshold for intersection observer (150px from top of element)
const DEFAULT_THRESHOLD_PX = 150

/** Clamps `n` to the inclusive range `[min, max]`. */
function clamp(n: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, n))
}

/**
 * Converts a pixel “visible height” target into a single `IntersectionObserver` threshold ratio
 * using the element’s `scrollHeight` (same behavior as the original pixel-only directive).
 */
function pixelThresholdToIntersectionRatio(thresholdPx: number, el: HTMLElement): number {
    const elementHeight = el.scrollHeight
    const actualThresholdPx = Math.min(thresholdPx, elementHeight)
    const ratio = actualThresholdPx / elementHeight
    return Number.isNaN(ratio) ? 0 : ratio
}

/**
 * Parses `binding.arg` into one `threshold` value for `IntersectionObserver`.
 *
 * - **Pixel mode** (digits only, e.g. `300`): `min(px, scrollHeight) / scrollHeight` — “when about
 *   this many pixels of the element’s box are visible” (legacy behavior).
 * - **Ratio mode** (suffix `%`, e.g. `40%`): `clamp(0, parseFloat / 100, 1)` — matches the API’s
 *   **`intersectionRatio`** (fraction of the target intersecting the root), not viewport height.
 */
function resolveIntersectThreshold(bindingArg: unknown, el: HTMLElement): number {
    const raw = bindingArg != null && bindingArg !== '' ? String(bindingArg).trim() : ''

    if (raw.endsWith('%')) {
        const pct = parseFloat(raw.slice(0, -1))
        if (Number.isNaN(pct)) {
            return pixelThresholdToIntersectionRatio(DEFAULT_THRESHOLD_PX, el)
        }
        return clamp(pct / 100, 0, 1)
    }

    const thresholdPx = raw ? parseInt(raw, 10) : DEFAULT_THRESHOLD_PX
    if (Number.isNaN(thresholdPx)) {
        return pixelThresholdToIntersectionRatio(DEFAULT_THRESHOLD_PX, el)
    }
    return pixelThresholdToIntersectionRatio(thresholdPx, el)
}

/**
 * Vue directive for detecting when elements scroll into view using Intersection Observer API.
 *
 * Triggers `binding.value()` when the observed intersection **crosses** the configured threshold
 * while `entry.isIntersecting` is true (see `IntersectionObserver` semantics).
 *
 * **Pixel mode (default):** threshold is derived from how many pixels of the element’s height
 * should be visible, then converted to a ratio — intuitive for “fire after ~N px scroll.”
 *
 * **Ratio mode:** pass a `%` suffix (e.g. `40%`) to use **`intersectionRatio`** directly (0–100%
 * of the element’s intersection with the viewport).
 *
 * @usage
 * ```vue
 * <template>
 *   <!-- Basic usage with default 150px threshold -->
 *   <div v-intersect="onElementVisible">
 *     Content that triggers callback when 150px is visible
 *   </div>
 *
 *   <!-- Custom pixel threshold -->
 *   <div v-intersect:300="onElementVisible">
 *     Content that triggers callback when 300px is visible
 *   </div>
 *
 *   <!-- Ratio: fire when ~40% of the element intersects the viewport -->
 *   <div v-intersect:40%="onElementVisible">
 *     Uses IntersectionObserver intersectionRatio
 *   </div>
 *
 *   <!-- Small pixel threshold for early detection -->
 *   <div v-intersect:50="onElementVisible">
 *     Content that triggers callback when just 50px is visible
 *   </div>
 * </template>
 *
 * <script setup>
 * const onElementVisible = () => {
 *   console.log('Element crossed the visibility threshold!')
 * }
 * </script>
 * ```
 *
 * @behavior
 * - The callback can run again when the element re-enters after leaving (threshold crossings).
 * - **Pixel mode:** if the pixel threshold exceeds the element’s height, behavior matches requiring
 *   the full element visible (ratio 1).
 * - The observer is disconnected on `unmounted`.
 *
 * @param {Function} binding.value - Callback when the threshold is crossed while intersecting
 * @param {string} [binding.arg] - Pixel count (e.g. `300`) or ratio percent (e.g. `40%`). Omit for 150px default.
 *
 * @example
 * // Trigger validation when enough of the review panel is visible
 * <section v-intersect:40%="onReviewVisible" />
 *
 * @example
 * <HeroSection v-intersect:200="startAnimation" />
 */
export default defineNuxtPlugin((nuxtApp) => {
    nuxtApp.vueApp.directive('intersect', {
        mounted(el: HTMLElement, binding) {
            if (typeof binding.value !== 'function') {
                // eslint-disable-next-line no-console
                console.warn('v-intersect directive requires a callback function')
                return
            }

            const threshold = resolveIntersectThreshold(binding.arg, el)

            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry?.isIntersecting) {
                        binding.value()
                    }
                },
                {
                    root: null,
                    rootMargin: '0px',
                    threshold,
                }
            )

            observer.observe(el)

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ;(el as any).__vueIntersectObserver__ = observer
        },
        unmounted(el: HTMLElement) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const observer = (el as any).__vueIntersectObserver__
            if (observer) {
                observer.disconnect()
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                delete (el as any).__vueIntersectObserver__
            }
        },
    })
})
